import { useEffect, useMemo, useRef, useState } from 'react';

import {
  EqualIcon,
  Modal,
  Pagination,
  RemoveIcon,
  Text,
  type TypeTextProps,
  toastService,
} from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { Table, TableTopField } from '~/components/common';

import { useAPI, useStableCallback } from '~/hooks/common';
import { useLoadingStore } from '~/store';
import { formatDate, formatNumber, getSettlementStorageDailyFeeDetailColumnDefs } from '~/utils';

import { TOAST_MESSAGE } from '~/constants';

import type {
  TypeGetStorageFeeDailySkusParams,
  TypeGetStorageFeeDailySkusResponse,
  TypeGetStorageFeeDailyTotalParams,
  TypeGetStorageFeeDailyTotalResponse,
} from '~/types/settlement.types';

import styles from './StorageDailyFeeDetailModal.module.scss';

/** 표 높이. 행이 많으면 표 안에서 스크롤한다. */
const TABLE_HEIGHT = 440;

const PAGE_SIZE = 100;

type TypeStorageDailyFeeDetailModalProps = {
  date: Date;
  onClose: () => void;
};

const StorageDailyFeeDetailModal = ({ date, onClose }: TypeStorageDailyFeeDetailModalProps) => {
  const setIsLoading = useLoadingStore((state) => state.setIsLoading);

  const { columnDefs } = useMemo(() => getSettlementStorageDailyFeeDetailColumnDefs(), []);

  const isFirstFetchRef = useRef(true);

  const [dailyStorageFeeSummary, setDailyStorageFeeSummary] = useState<
    TypeGetStorageFeeDailyTotalResponse['data'] | null
  >(null);
  const [dailyStorageFeeData, setDailyStorageFeeData] = useState<TypeGetStorageFeeDailySkusResponse['data'] | null>(
    null
  );
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalCount: 0,
    limit: PAGE_SIZE,
  });

  const localOpen = !!dailyStorageFeeSummary && !!dailyStorageFeeData;

  const fetchInitialData = async () => {
    const results = await Promise.allSettled([fetchStorageDailyFeeTotal(), fetchStorageDailyFeeSkus()]);

    const hasError = results.some((result) => result.status === 'rejected');
    if (hasError) {
      onClose();
      return;
    }

    isFirstFetchRef.current = false;
  };

  const getStorageFeeDailyTotal = useAPI<TypeGetStorageFeeDailyTotalParams, TypeGetStorageFeeDailyTotalResponse>({
    endpoint: '/api-inventory-agg/v1/storage-fee/daily/total',
    method: 'get',
  });

  const getStorageFeeDailySkus = useAPI<TypeGetStorageFeeDailySkusParams, TypeGetStorageFeeDailySkusResponse>({
    endpoint: '/api-inventory-agg/v1/storage-fee/daily/skus',
    method: 'get',
  });
  const fetchStorageDailyFeeTotal = async (feeDate: Date = date) => {
    try {
      const formattedDate = formatDate(feeDate, 'YYYYMMDD');

      const { data } = await getStorageFeeDailyTotal({ params: { feeDate: formattedDate ?? '' } });
      setDailyStorageFeeSummary(data);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });

      if (isFirstFetchRef.current) {
        return Promise.reject(new Error());
      }
    }
  };

  const fetchStorageDailyFeeSkus = async (currentPage = 0) => {
    try {
      setIsLoading(true);

      const params = {
        feeDate: formatDate(date, 'YYYYMMDD') ?? '',
        cursor: currentPage,
        limit: PAGE_SIZE,
      };

      const { data, pagination } = await getStorageFeeDailySkus({ params });

      if (data.length === 0) {
        toastService.addToast({
          message: '조회 가능한 보관비가 없습니다.',
        });
        onClose();
        return;
      }

      setDailyStorageFeeData(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: pagination.cursor,
        totalCount: pagination.listTotal,
      }));
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });

      if (isFirstFetchRef.current) {
        return Promise.reject(new Error());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchInitialData = useStableCallback(fetchInitialData);

  useEffect(() => {
    handleFetchInitialData();
  }, [handleFetchInitialData]);

  return (
    <Modal
      title={`${formatDate(date, 'YYYY년 MM월 DD일')} 보관비 상세`}
      size="xlarge"
      open={localOpen}
      onConfirm={onClose}
    >
      <div className={styles['contents-container']}>
        {dailyStorageFeeSummary && (
          <div className={styles['summary-section']}>
            <SummaryItem label="보관비 합계" amount={{ value: dailyStorageFeeSummary.totalStorageFee }} />
            <RemoveIcon size={16} color="gray400" />
            <SummaryItem label="면제 금액" amount={{ value: dailyStorageFeeSummary.unchargedStorageFee }} />
            <EqualIcon size={16} color="gray400" />
            <SummaryItem
              label="실제 과금액"
              amount={{ value: dailyStorageFeeSummary.chargedStorageFee, color: 'error' }}
            />
          </div>
        )}
        <div className={styles['view-section']}>
          <TableTopField dataTotalCount={pagination.totalCount} />
          <Table
            className={styles['table']}
            data={dailyStorageFeeData}
            columns={columnDefs}
            height={TABLE_HEIGHT}
            rowStatusType="NO_DATA"
          />
        </div>
        {pagination.totalCount > 0 && (
          <div className={styles['view-actions-section']}>
            <Pagination
              currentPage={pagination.currentPage + 1}
              totalCount={pagination.totalCount ?? 0}
              pageSize={PAGE_SIZE}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                const updatedPage = e.currentTarget.value ? Number(e.currentTarget.value) - 1 : 0;
                fetchStorageDailyFeeSkus(updatedPage);
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default StorageDailyFeeDetailModal;

const SummaryItem = ({
  label,
  amount: { value, color = 'primary' },
}: {
  label: string;
  amount: { value: number; color?: TypeTextProps['color'] };
}) => {
  return (
    <div className={styles['summary-item']}>
      <Text className={styles['summary-item-label']} color="secondary">
        {label}
      </Text>
      <Text className={styles['summary-item-amount']} weight="semi-bold" color={color}>
        {formatNumber(value, '원')}
      </Text>
    </div>
  );
};

const storageFeeDailyTotalResponse: TypeGetStorageFeeDailyTotalResponse = {
  data: { totalStorageFee: 186000, unchargedStorageFee: 15000, chargedStorageFee: 171000 },
};

// 구간별 과금액은 일부 구간만 내려오는 경우('-' 표시)를 섞는다.
const storageFeeDailySkusResponse: TypeGetStorageFeeDailySkusResponse = {
  data: [
    {
      skuName: '[e2e] 유기농 바나나 1kg',
      skuCode: 'SKU0000000001',
      skuBarcode: '8801234500011',
      totalStorageFee: 52000,
      chargedStorageFee: 52000,
      storageFeePerRange: { RANGE_001: 30000, RANGE_002: 12000, RANGE_003: 6000, RANGE_004: 4000 },
      quantity: 1200,
      cbm: 12.5,
    },
    {
      skuName: '[e2e] 무항생제 대란 30구',
      skuCode: 'SKU0000000002',
      skuBarcode: '8801234500028',
      totalStorageFee: 41000,
      chargedStorageFee: 26000,
      storageFeePerRange: { RANGE_001: 26000 },
      quantity: 830,
      cbm: 8.25,
    },
    {
      skuName: '[e2e] 냉동 만두 1.2kg',
      skuCode: 'SKU0000000003',
      skuBarcode: null,
      totalStorageFee: 38000,
      chargedStorageFee: 38000,
      storageFeePerRange: { RANGE_002: 20000, RANGE_003: 18000 },
      quantity: 540,
      cbm: 6.1,
    },
    {
      skuName: null,
      skuCode: 'SKU0000000004',
      skuBarcode: '8801234500042',
      totalStorageFee: 30000,
      chargedStorageFee: 30000,
      storageFeePerRange: { RANGE_004: 30000 },
      quantity: 210,
      cbm: 3.75,
    },
    {
      skuName: '[e2e] 제주 감귤 3kg',
      skuCode: 'SKU0000000005',
      skuBarcode: '8801234500059',
      totalStorageFee: 25000,
      chargedStorageFee: 25000,
      storageFeePerRange: { RANGE_001: 15000, RANGE_002: 10000 },
      quantity: 400,
      cbm: 4,
    },
  ],
  pagination: { listView: 100, listTotal: 5, cursor: 0 },
};

registerMockAPI<TypeGetStorageFeeDailyTotalResponse>({
  method: 'get',
  endpoint: '/api-inventory-agg/v1/storage-fee/daily/total',
  label: '정산/보관비조회/일별보관비합계',
  scenarios: [{ name: '정상 응답', response: storageFeeDailyTotalResponse }],
});

registerMockAPI<TypeGetStorageFeeDailySkusResponse>({
  method: 'get',
  endpoint: '/api-inventory-agg/v1/storage-fee/daily/skus',
  label: '정산/보관비조회/일별SKU보관비',
  scenarios: [
    { name: '정상 응답 — 5건', response: storageFeeDailySkusResponse },
    { name: '데이터 없음', response: { data: [], pagination: { listView: 100, listTotal: 0, cursor: 0 } } },
  ],
});
